// Copyright (c) 2026 aiApas LLC. All Rights Reserved.
// GuardRok — Proprietary and Confidential. Patent Pending.
// Unauthorized use or distribution is prohibited.

import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { GuardRokIcon } from '../components/GuardRokLogo'
import ChatWidget from '../components/ChatWidget'
import { RefreshCw, Copy, Check, Shield, AlertTriangle } from 'lucide-react'

const CWS_URL = 'https://chromewebstore.google.com/detail/guardrok/dldblbcpofdflbbhpahbokamikpakhdh'
const EXT_VERSION = '1.0.4'
const SIGNUP_URL = 'https://app.guardrok.com/signup'
const ALT_URL = 'https://tryguardrok.com/signup'

// Prompts that SHOULD be blocked by GuardRok
const BLOCK_POOL: Record<string, { color: string; bg: string; prompts: string[] }> = {
  'SSN / National ID': {
    color: '#ef4444',
    bg: '#450a0a',
    prompts: [
      'My SSN is 519-38-7042. Help me fill out this W-2 form.',
      'Add SSN 617-44-9023 to this HR intake form.',
      'Record national insurance number AB 12 34 56 C in this payroll system.',
      'Check if 078-05-1120 is a valid SSN for our records.',
      'Buyer Kevin Larsson | SSN (Kevin): 512-**-9037 | Pre-approval: $875K | Credit: 742. Draft buyer intro letter.',
    ],
  },
  'Credit / Bank Card': {
    color: '#f59e0b',
    bg: '#451a03',
    prompts: [
      'Charge $450 to card number 4532 1234 5678 9012 exp 09/27 CVV 394.',
      'My Visa is 4111-1111-1111-1111 expiry 12/26. Help me set up autopay.',
      'Process a refund to card 5500 0000 0000 0004, expiry 12/26 CVV 123.',
      'Run a $1,200 charge on Mastercard 5105105105105100 CVV 521.',
    ],
  },
  'API Key / Secret': {
    color: '#8b5cf6',
    bg: '#2e1065',
    prompts: [
      "Here's my OpenAI key sk-proj-Abc123XYZdefGHI456. What's wrong with my code?",
      'My AWS access key is AKIAIOSFODNN7EXAMPLE — debug this Lambda for me.',
      'Stripe live key sk_live_4eC39HqLyjWDarjtT1zdp7dc — is my webhook correct?',
      'GitHub token ghp_abc123DEF456ghi789JKL — why is this push failing?',
      "My database password is P@ssw0rd#2024! — why can't I connect to prod?",
    ],
  },
  'Patient PHI — Healthcare': {
    color: '#10b981',
    bg: '#052e16',
    prompts: [
      'Patient John Smith DOB 15/03/1972 MRN 00234891 — Type 2 diabetes. Draft discharge summary.',
      'Write a referral for Mary Jones NPI 1234567890, diagnosed with major depressive disorder.',
      'PHI: patient Sarah Connor DOB 1965-03-15, HIV positive, last viral load undetectable.',
      'Draft therapy note for patient 38291, session topic: suicidal ideation risk assessment.',
    ],
  },
  'PII — Personal Data': {
    color: '#3b82f6',
    bg: '#1e3a5f',
    prompts: [
      'Write to James Whitfield, DOB 12 April 1985, passport GB29398106, 42 Elm St London.',
      "Driver's licence DL: A1234567, DOB 03/15/1990. Help fill this rental form.",
      'Send a letter to Sarah Connor, 123 Sunset Blvd, DOB 1965-03-15, phone 555-0192.',
      'Full name: Emily Rodriguez, DOB 22 Jun 1978, NI AB123456C, maiden name Santos.',
    ],
  },
  'Financial — MNPI / Wire': {
    color: '#f59e0b',
    bg: '#451a03',
    prompts: [
      'IBAN GB29 NWBK 6016 1331 9268 19, sort code 60-16-13. Process this wire transfer.',
      'Wire $250,000 to account 9876543210 at Chase, routing 021000021.',
      'We are acquiring TechNova for $2.1B. Deal closes Friday. Draft all-hands talking points before the public announcement.',
      'M&A due diligence for acquisition of Crestwood Medical Group. Deal: $340M. Undisclosed: 3 EEOC charges, $8.2M unrecorded liabilities. Summarise for board memo.',
    ],
  },
  'Confidential IP / Legal': {
    color: '#9ca3af',
    bg: '#111827',
    prompts: [
      'ATTORNEY-CLIENT PRIVILEGE: settlement offer details enclosed. Summarise risks.',
      'Do not share externally — M&A term sheet for Target Co at $120M. Review clauses.',
      "Client AlloyTech's proprietary titanium alloy formula (87% Ti, 6.2% Al, 3.1% V) stolen by former employee Kevin Strand. Draft TRO.",
      'Audit finding: client Meridian Corp $6.1M inventory overstatement not yet disclosed to SEC. Engagement partner review pending. Draft workpaper.',
    ],
  },
  'HR / Employee Records': {
    color: '#ec4899',
    bg: '#4a044e',
    prompts: [
      'Employee Tom Baker, salary £85,000, on FMLA since March 2026. Draft return-to-work plan.',
      'Sarah Chen, Senior Engineer, base $187K, bonus 20%, RSUs $420K. She is being PIPed. Draft termination letter.',
      'Our employee John Doe has ADA accommodation for anxiety disorder. Draft adjusted schedule.',
      'Compensation data: Dev team salaries range £60k–£110k. Write a board benchmarking report.',
    ],
  },
  'Real Estate Intel': {
    color: '#fb923c',
    bg: '#431407',
    prompts: [
      "My buyer Kevin Huang won't go above $1.15M but told the seller $1.05M is his ceiling. How do I close without revealing his real max?",
      'Seller Sarah Mitchell needs to close by June 30 — divorce. Net equity ~$340K. How do I use this in negotiation?',
      'Pocket listing: 22 Oak Lane off-market at $820K, seller motivated. Help me draft exclusive offer before it hits MLS.',
    ],
  },
}

// Prompts that SHOULD PASS through GuardRok (legitimate professional work)
const PASS_POOL: Record<string, { prompts: string[] }> = {
  'Healthcare / Medical': {
    prompts: [
      'What is Hierarchical Condition Category (HCC) coding and how does it affect Medicare Advantage risk adjustment?',
      'What are the major drug interactions between warfarin and amiodarone?',
      "Draft a HIPAA-compliant response to a third-party employer inquiry about an employee's health status. Decline to confirm or deny. Cite 45 CFR §164.512. No patient name or diagnosis.",
      "Help me draft a privacy notice explaining patients' rights under HIPAA for our clinic waiting room.",
      'What is the ICD-10 code for type 2 diabetes with chronic kidney disease stage 3?',
    ],
  },
  'Legal / Compliance': {
    prompts: [
      'Explain the legal elements required to prove trade secret misappropriation under the DTSA.',
      'What are the key differences between an NDA and a non-compete agreement?',
      'Draft a standard mutual NDA template for two companies exploring a business partnership.',
      'Summarise the main obligations under GDPR Article 28 for data processors.',
      'What is attorney-client privilege and when does it apply?',
    ],
  },
  'Finance / Banking': {
    prompts: [
      'Explain the difference between EBITDA and operating income.',
      'What is Basel III and how does it affect bank capital requirements?',
      'What are the main differences between a revolver and a term loan?',
      'Help me understand LTV, DTI, and origination fee in a loan agreement.',
      'Draft a standard wire transfer instruction template for our finance team (no account numbers).',
    ],
  },
  'HR / People Ops': {
    prompts: [
      'Draft a professional job description for a Senior Software Engineer at a Series B startup.',
      'What are best practices for running a structured interview to reduce bias?',
      'Help me write a 30-60-90 day onboarding plan for a new Sales Director.',
      "What are an employer's obligations under the FMLA?",
      'Draft a company-wide email announcing our new flexible work policy.',
    ],
  },
  'Technology / Engineering': {
    prompts: [
      'Explain the difference between SQL and NoSQL databases.',
      'Help me write a Python function to parse JSON from an API response.',
      'What is the time complexity of a binary search algorithm?',
      'Explain how JWT authentication works.',
      'Review this React component for performance issues — no credentials included.',
    ],
  },
  'Real Estate': {
    prompts: [
      'Draft a buyer intro letter for the sellers of 14 Walnut Crest Dr. We are a young family who love the neighbourhood schools.',
      'What documentation is typically required for a mortgage pre-approval?',
      "Explain the difference between a buyer's agent and a listing agent.",
      'Help me write a property listing description for a 3-bed 2-bath home with a pool in Plano TX.',
      'What is the typical timeline for closing on a house in Texas?',
    ],
  },
  'Consulting / Strategy': {
    prompts: [
      'What frameworks should I use for a competitive landscape analysis?',
      'Help me structure a McKinsey-style problem statement for a market entry project.',
      'Draft an executive summary template for a strategy engagement kickoff.',
      'Explain the difference between a top-down and bottom-up revenue model.',
      "What are the key elements of a Porter's Five Forces analysis?",
    ],
  },
  'Accounting / Audit': {
    prompts: [
      'Explain what a material weakness in internal controls means under SOX Section 404.',
      'What is the difference between a qualified and unqualified audit opinion?',
      'Describe the steps in a standard accounts payable audit procedure.',
      'Help me draft a client engagement letter for a financial statement review.',
      'What are the key differences between GAAP and IFRS revenue recognition?',
    ],
  },
}

interface PromptCard {
  cat: string
  color: string
  bg: string
  prompt: string
  shouldBlock: boolean
}

function shuffle<T>(arr: T[]): T[] {
  return [...arr].sort(() => Math.random() - 0.5)
}

function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]
}

function generateCards(): PromptCard[] {
  // 3 should-block + 3 should-pass, shuffled together
  const blockCats = shuffle(Object.keys(BLOCK_POOL)).slice(0, 3)
  const passCats = shuffle(Object.keys(PASS_POOL)).slice(0, 3)
  const blockCards: PromptCard[] = blockCats.map(cat => {
    const { color, bg, prompts } = BLOCK_POOL[cat]
    return { cat, color, bg, prompt: pick(prompts), shouldBlock: true }
  })
  const passCards: PromptCard[] = passCats.map(cat => {
    const { prompts } = PASS_POOL[cat]
    return { cat, color: '#22c55e', bg: '#052e16', prompt: pick(prompts), shouldBlock: false }
  })
  return shuffle([...blockCards, ...passCards])
}

function PromptCardItem({ card }: { card: PromptCard }) {
  const [copied, setCopied] = useState(false)

  function copy() {
    navigator.clipboard.writeText(card.prompt).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const isBlock = card.shouldBlock
  const borderColor = isBlock ? '#2a1215' : '#0d2b1e'
  const badgeColor = isBlock ? '#E63946' : '#22c55e'
  const badgeBg = isBlock ? '#451a1e' : '#052e16'
  const badgeLabel = isBlock ? 'Should be blocked' : 'Should pass through'

  return (
    <div
      style={{
        background: '#0d1117',
        border: `1px solid ${borderColor}`,
        borderRadius: 10,
        padding: '16px 18px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 10,
        }}
      >
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            textTransform: 'uppercase' as const,
            letterSpacing: '1px',
            padding: '3px 9px',
            borderRadius: 20,
            background: card.bg,
            color: card.color,
          }}
        >
          {card.cat}
        </span>
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            textTransform: 'uppercase' as const,
            letterSpacing: '1px',
            color: badgeColor,
            background: badgeBg,
            padding: '3px 9px',
            borderRadius: 20,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: badgeColor,
              display: 'inline-block',
            }}
          />
          {badgeLabel}
        </span>
      </div>
      <div
        style={{
          fontSize: 13,
          color: '#9ca3af',
          lineHeight: 1.6,
          fontFamily: 'monospace',
          background: '#111827',
          borderRadius: 6,
          padding: '10px 12px',
          marginBottom: 8,
          userSelect: 'all' as const,
        }}
      >
        {card.prompt}
      </div>
      <button
        onClick={copy}
        style={{
          background: 'transparent',
          border: `1px solid ${copied ? '#22c55e' : '#1f2937'}`,
          color: copied ? '#22c55e' : '#6b7280',
          fontSize: 11,
          padding: '4px 10px',
          borderRadius: 6,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          transition: 'all 0.15s',
        }}
      >
        {copied ? <Check size={11} /> : <Copy size={11} />}
        {copied ? 'Copied ✓' : 'Copy prompt'}
      </button>
    </div>
  )
}

export default function Beta() {
  const [cards, setCards] = useState<PromptCard[]>([])
  const [spinning, setSpinning] = useState(false)

  useEffect(() => {
    setCards(generateCards())
  }, [])

  function regenerate() {
    setSpinning(true)
    setTimeout(() => {
      setCards(generateCards())
      setSpinning(false)
    }, 300)
  }

  const S = {
    page: {
      minHeight: '100vh',
      background: '#030712',
      color: '#d1d5db',
      fontFamily: 'system-ui, sans-serif',
      padding: '40px 20px',
    },
    wrap: { maxWidth: 740, margin: '0 auto' },
    section: {
      fontSize: 11,
      fontWeight: 700,
      textTransform: 'uppercase' as const,
      letterSpacing: '1.5px',
      color: '#E63946',
      marginBottom: 16,
    },
    step: {
      display: 'flex',
      gap: 14,
      alignItems: 'flex-start' as const,
      background: '#0d1117',
      border: '1px solid #1f2937',
      borderRadius: 10,
      padding: '16px 18px',
    },
    num: {
      width: 28,
      height: 28,
      borderRadius: '50%',
      background: '#E63946',
      color: '#fff',
      fontSize: 13,
      fontWeight: 800,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
  }

  return (
    <div style={S.page}>
      <div style={S.wrap}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 32 }}>
          <GuardRokIcon size={32} />
          <div style={{ fontSize: 22, fontWeight: 800, color: '#fff' }}>
            Guard<span style={{ color: '#E63946' }}>Rok</span>
            <span
              style={{
                background: '#451a1e',
                color: '#E63946',
                fontSize: 10,
                fontWeight: 700,
                textTransform: 'uppercase' as const,
                letterSpacing: '1.5px',
                padding: '3px 10px',
                borderRadius: 20,
                marginLeft: 8,
                verticalAlign: 'middle',
              }}
            >
              Beta
            </span>
            <span
              style={{
                background: '#0d1117',
                color: '#4b5563',
                fontSize: 9,
                fontWeight: 600,
                padding: '2px 8px',
                borderRadius: 20,
                marginLeft: 6,
                verticalAlign: 'middle',
                border: '1px solid #1f2937',
              }}
            >
              ext v{EXT_VERSION}
            </span>
          </div>
        </div>

        {/* Hero */}
        <div style={{ marginBottom: 36 }}>
          <h2
            style={{
              fontSize: 28,
              fontWeight: 800,
              color: '#fff',
              lineHeight: 1.3,
              marginBottom: 10,
            }}
          >
            Welcome, Beta Tester 👋
            <br />
            Let's make sure you're protected.
          </h2>
          <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.7 }}>
            This guide walks you through setting up GuardRok and testing it with real prompts. Every
            time you click "Generate Test Prompts" you'll get a fresh set — try different ones each
            session.
          </p>
        </div>

        {/* Steps */}
        <div style={S.section}>Step-by-step setup</div>
        <div
          style={{ display: 'flex', flexDirection: 'column' as const, gap: 12, marginBottom: 36 }}
        >
          {[
            {
              n: '1',
              title: 'Install the GuardRok extension',
              body: (
                <>
                  <a
                    href={CWS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#E63946', fontWeight: 700 }}
                  >
                    Add GuardRok to Chrome (free, permanent install)
                  </a>{' '}
                  — installs like any normal extension and stays installed across Chrome restarts.
                  After install, Chrome hides new extensions in the{' '}
                  <strong>puzzle piece menu (⊕)</strong> by default — click it, find GuardRok, and
                  click the <strong>pin icon</strong> to keep the shield in your toolbar.
                </>
              ),
              tip: "💡 Don't see the shield after installing? Click the ⊕ puzzle piece icon top-right → pin GuardRok → the red shield will appear.",
            },
            {
              n: '2',
              title: 'Sign in to activate protection',
              body: (
                <>
                  Click the GuardRok shield in your toolbar → enter your work email and password →
                  click <strong>Sign In</strong>. The shield turns{' '}
                  <strong style={{ color: '#E63946' }}>red</strong> when you're protected. No
                  account yet?{' '}
                  <a
                    href={SIGNUP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#E63946' }}
                  >
                    Sign up at app.guardrok.com
                  </a>{' '}
                  or{' '}
                  <a
                    href={ALT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#E63946' }}
                  >
                    tryguardrok.com
                  </a>
                  . If you're already signed into the dashboard, the extension connects
                  automatically.
                </>
              ),
              tip: null,
            },
            {
              n: '3',
              title: 'Reload your AI tool tab',
              body: (
                <>
                  After installing and signing in,{' '}
                  <strong>reload your ChatGPT / Claude / Gemini tab</strong> (press F5 or Ctrl+R).
                  The extension only activates on tabs loaded after install — existing tabs won't be
                  protected until you reload them.
                </>
              ),
              tip: '💡 Still not blocking after reload? Open the extension popup and confirm the shield is red (signed in). Gray shield = not signed in.',
            },
            {
              n: '4',
              title: 'Open an AI tool',
              body: (
                <>
                  GuardRok works on 16 AI tools:{' '}
                  {[
                    'ChatGPT',
                    'Claude.ai',
                    'Gemini',
                    'Copilot',
                    'Perplexity',
                    'Grok',
                    'Meta AI',
                    'Mistral',
                    'DeepSeek',
                    'HuggingFace',
                    'Poe',
                    'Character.AI',
                    'You.com',
                    'Pi',
                    'GitHub Copilot',
                    'Bing',
                  ].map((t, i) => (
                    <span key={t}>
                      <code
                        style={{
                          background: '#1f2937',
                          color: '#f3f4f6',
                          fontSize: 12,
                          padding: '2px 6px',
                          borderRadius: 4,
                          fontFamily: 'monospace',
                        }}
                      >
                        {t}
                      </code>
                      {i < 15 ? ' · ' : ''}
                    </span>
                  ))}{' '}
                  Open any of these in your browser.
                </>
              ),
              tip: null,
            },
            {
              n: '5',
              title: 'Test with the prompts below',
              body: (
                <>
                  Click <strong style={{ color: '#E63946' }}>Generate Test Prompts</strong> to get a
                  fresh set of test prompts. Copy a prompt, paste it into the AI tool, and press{' '}
                  <strong>Enter</strong> or click the <strong>send button</strong>. GuardRok
                  intercepts both — the prompt is stopped before it reaches the AI and a red block
                  modal appears. ✅
                </>
              ),
              tip: null,
            },
          ].map(({ n, title, body, tip }) => (
            <div key={n} style={S.step}>
              <div style={S.num}>{n}</div>
              <div>
                <div style={{ color: '#f3f4f6', fontSize: 14, fontWeight: 700, marginBottom: 4 }}>
                  {title}
                </div>
                <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.6 }}>{body}</div>
                {tip && (
                  <div
                    style={{
                      background: '#0c1a2e',
                      border: '1px solid #1e3a5f',
                      borderRadius: 8,
                      padding: '10px 14px',
                      marginTop: 8,
                      fontSize: 12,
                      color: '#60a5fa',
                      lineHeight: 1.6,
                    }}
                  >
                    {tip}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Incognito section */}
        <div style={S.section}>Testing in incognito (private) mode</div>
        <div
          style={{
            background: '#0d1117',
            border: '1px solid #1f2937',
            borderRadius: 10,
            padding: '20px 22px',
            marginBottom: 36,
          }}
        >
          <div
            style={{
              color: '#f3f4f6',
              fontSize: 15,
              fontWeight: 700,
              marginBottom: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <AlertTriangle size={16} color="#f59e0b" /> Enable GuardRok in Incognito mode
          </div>
          <ol
            style={{ paddingLeft: 20, display: 'flex', flexDirection: 'column' as const, gap: 6 }}
          >
            {[
              <>
                Open a new tab → type <strong>chrome://extensions</strong> → press Enter
              </>,
              <>
                Find <strong>GuardRok AI Firewall</strong> → click <strong>Details</strong>
              </>,
              <>
                Scroll down → toggle <strong>"Allow in Incognito"</strong> → ON
              </>,
              <>
                Open a new <strong>Incognito window</strong> (Ctrl+Shift+N / Cmd+Shift+N)
              </>,
              <>Go to ChatGPT or Claude.ai → paste a test prompt → it should still be blocked ✅</>,
            ].map((step, i) => (
              <li key={i} style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.8 }}>
                {step}
              </li>
            ))}
          </ol>
          <div
            style={{
              background: '#0c1a2e',
              border: '1px solid #1e3a5f',
              borderRadius: 8,
              padding: '10px 14px',
              marginTop: 12,
              fontSize: 12,
              color: '#60a5fa',
              lineHeight: 1.6,
            }}
          >
            💡 Enterprise customers: your IT admin can force this on for all devices via Google
            Admin Console — individual employees don't need to do this step.
          </div>
        </div>

        {/* Prompt generator */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 16,
            flexWrap: 'wrap' as const,
            gap: 12,
          }}
        >
          <div style={S.section as React.CSSProperties & { marginBottom: number }}>
            Test prompts — click to regenerate
          </div>
          <button
            onClick={regenerate}
            style={{
              background: '#E63946',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '10px 22px',
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <RefreshCw
              size={14}
              style={{ animation: spinning ? 'spin 0.6s linear infinite' : 'none' }}
            />
            Generate Test Prompts
          </button>
        </div>

        <div
          style={{ display: 'flex', flexDirection: 'column' as const, gap: 10, marginBottom: 36 }}
        >
          {cards.map((card, i) => (
            <PromptCardItem key={i} card={card} />
          ))}
        </div>

        {/* What to expect */}
        <div style={S.section}>What should happen</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 36 }}>
          {[
            {
              dot: '#E63946',
              title: 'Prompt is blocked',
              body: 'A red GuardRok modal pops up. The prompt never reaches the AI. This is the correct behaviour. Click "Got it — clear prompt" or edit your prompt.',
            },
            {
              dot: '#22c55e',
              title: 'Safe prompts go through',
              body: 'Normal questions with no sensitive data work fine. GuardRok only stops prompts that contain PII, credentials, financial data, or confidential information.',
            },
            {
              dot: '#E63946',
              title: 'Incognito also blocks',
              body: 'Once you enable "Allow in Incognito", the same protection applies in private windows. Switching to incognito should NOT bypass GuardRok.',
            },
            {
              dot: '#E63946',
              title: 'Sign out = blocked',
              body: 'If you sign out of GuardRok, all AI prompts are blocked until you sign back in. This is intentional — unauthenticated = no access.',
            },
          ].map(({ dot, title, body }) => (
            <div
              key={title}
              style={{
                background: '#0d1117',
                border: '1px solid #1f2937',
                borderRadius: 10,
                padding: '14px 16px',
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#f3f4f6',
                  marginBottom: 4,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: dot,
                    display: 'inline-block',
                  }}
                />
                {title}
              </div>
              <p style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5, margin: 0 }}>{body}</p>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div
          style={{
            textAlign: 'center' as const,
            fontSize: 12,
            color: '#374151',
            paddingTop: 24,
            borderTop: '1px solid #111827',
          }}
        >
          Questions? Email{' '}
          <a
            href="mailto:support@guardrok.com"
            style={{ color: '#6b7280', textDecoration: 'none' }}
          >
            support@guardrok.com
          </a>
          {' · '}
          <Link to="/dashboard" style={{ color: '#6b7280', textDecoration: 'none' }}>
            Open Dashboard
          </Link>
          {' · '}
          <Link to="/" style={{ color: '#6b7280', textDecoration: 'none' }}>
            guardrok.com
          </Link>
        </div>
      </div>
      <ChatWidget />
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  )
}
